import { useEffect, useState, useMemo, useCallback, SyntheticEvent, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as Sentry from '@sentry/react';
import { LocalErrorBoundary } from 'components/ErrorBoundary';

import usePrevious from 'hooks/usePrevious';

import { getAuditReport, useReportsStore } from 'hooks/useReportsStore';

import { isEmpty, set, uniqBy } from 'lodash';
import Loading from 'components/Loading';
import ReportDataTable from './components/ReportDataTable';
import { HoneReportFormats, HoneReportTypes, MENU_ICONS } from '../../../constants';
import {
  abilityCan,
  BALANCE_SHEET_PRESETS,
  currencyFormatter2Decimals,
  findBSPresetDates,
  FIVE_SECONDS,
  percentageFormatter,
  reportTitleToUrl,
  showToast,
  sortReportTitles,
  TOAST_ERROR,
  BalanceSheetPreset,
} from 'lib/utils';

import toast from 'react-hot-toast';
import { HoneReportTimeframes } from 'domain/models';
import { fetchReportById, performReportUpdate, useActiveReportStore } from 'hooks/useActiveReportStore';
import { shallow } from 'zustand/shallow';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocationsStore } from 'hooks/useLocationsStore';

import { useQueryState } from 'hooks/useQueryState';
import { useReportGraphStore } from 'hooks/useReportGraphStore';

import { ReportNotFoundError } from 'domain/errors/report-not-found-error';
import { QueryStatus } from '@tanstack/react-query';
import qs from 'qs';
import ReportDatesModal from 'components/ReportDatesModal';

import {
  extractReportIds,
  HoneAbilityActions,
  HoneAbilitySubjects,
  ReportRequest,
  ReportTypes,
  commonUtil,
  VendorTemplate,
} from '@hone-automation/common';
import { useAuthContext } from 'context/useAuthContext';

import KSHeader from '../../components/KSHeader';
import STBTools from 'presentation/components/SectionToolbar/STBTools';
import { SlidingPane } from 'components/SlidingPane';
import { differenceInSeconds, parseJSON } from 'date-fns';
import useActiveActions from 'hooks/useActiveActions';
import { Report } from 'lib/honeTableUtils';
import ValidationReportResults from 'components/ValidationReportResults';
import { useGetReportConfiguration } from '../../../hooks';
import { ReportConfiguration } from '@hone-automation/common/lib/Reports';
import { ActionProps } from 'presentation/components/SectionToolbar/STBTools/STBTools';
import { IconAuditNew, IconValidate } from 'components/Icons';
import APAgingReportConfiguration from '../../components/APAgingReportConfiguration';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { mappedReports } from 'lib/reportUtils';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: (...args: Array<unknown>) => void;
}

function ErrorFallback({ error }: FallbackProps) {
  return (
    <div role="alert">
      <p>This report is being updated, please try again in a few minutes.</p>
      <pre style={{ display: process.env.NODE_ENV === 'development' ? 'block' : 'none' }}>{error.message}</pre>
    </div>
  );
}

interface Props {
  reports: Map<string, HoneReportSummary[]>;
  columnHeaderTitle?: string;
  customHeaders?: string[];
  status: QueryStatus;
}

function OtherReportsView({ reports, columnHeaderTitle = '', customHeaders, status }: Props) {
  const { enableVendorReports } = useFlags();
  const { currentLocationAbilities } = useAuthContext();

  const [showValidationPane, setShowValidationPane] = useState<boolean>(false);

  const [validateReportError] = useState<string | null>(null);

  const [_, setAllReportsSelected] = useQueryState<boolean>('allReports', false);

  const [auditMode, setAuditMode] = useQueryState('audit');
  const [validation] = useQueryState('validate');
  const [currentAPReport, setCurrentAPReport] = useState<'AP Aging' | VendorTemplate>('AP Aging');
  const [configurationId] = useQueryState<string>('configurationId');
  const [limit] = useQueryState<number>('limit');

  const { activeActions } = useActiveActions();
  const showPercentageValues = activeActions.includes('lines');
  const showMoneyValues = activeActions.includes('bars');

  const [localLoading, setLocalLoading] = useState<boolean>(false);

  // Use a direct selector for validationReportData to ensure re-renders when validation changes
  const validationReportData = useActiveReportStore(state => state.currentReport?.validation);

  const { currentLocationId, currentLocation } = useLocationsStore(
    state => ({ currentLocationId: state.currentLocationId, currentLocation: state.currentLocation }),
    shallow
  );

  const { data: reportConfiguration } = useGetReportConfiguration(currentLocationId);

  const location = useLocation();
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const { locationId, reportId, reportType } = useParams<{
    locationId: string;
    reportId: string;
    reportType: string;
  }>();

  const navigateTo = useNavigate();
  const isPLReport = ['income-statement', 'monthly', 'weekly'].includes(reportType!);

  const [chartCollapsed] = useQueryState('chartCollapsed', 'false');

  const isBookkeeper = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  const [, setDateRange] = useQueryState<string>('dateRange');
  const isBalanceSheetPreset = Object.keys(BALANCE_SHEET_PRESETS).includes(reportId!);

  const reportsError = useReportsStore(state => state.error);
  const { selectedReport, setOpenDateRangeModal, setReportsConfiguration } = useReportsStore(
    state => ({
      selectedReport: state.selectedReport,
      setOpenDateRangeModal: state.setOpenDateRangeModal,
      openDateRangeModal: state.openDateRangeModal,
      setReportsConfiguration: state.setReportsConfiguration,
    }),
    shallow
  );

  const isYtd = selectedReport?.timeframe === HoneReportTimeframes.YTDMonthly;

  const {
    reportStatus,
    allCategories,
    getGroupReport,
    setYearPlotted,
    setRefreshingReport,
    rangeNoData,
    setStatus,
    currentReport,
    setReportPayload,
    otherReportPayload,
    setOtherReportPayload,
  } = useActiveReportStore(
    state => ({
      setRefreshingReport: state.setRefreshingReport,
      setStatus: state.setStatus,
      rangeNoData: state.rangeNoData,
      reportStatus: state.status,
      allCategories: state.allCategories,
      getGroupReport: state.getGroupReport,
      setYearPlotted: state.setYearPlotted,
      currentReport: state.currentReport,
      setReportPayload: state.setReportPayload,
      otherReportPayload: state.otherReportPayload,
      setOtherReportPayload: state.setOtherReportPayload,
    }),
    shallow
  );

  const [statusUpdates] = useState<Record<string, string>>({});
  const lastRequestRef = useRef<string | null>(null);

  const allReports = reports.get(isPLReport ? 'pl' : reportType!) || [];
  const titles = uniqBy(allReports, 'title');

  const uniqueTitles = sortReportTitles(titles);

  const [titleFilter] = useQueryState<string>('type', reportTitleToUrl(uniqueTitles[0]?.title || ''));
  const [templateId] = useQueryState<string>('templateId');

  const filteredTitleFilter = templateId
    ? reportTitleToUrl(uniqueTitles.find(props => props.templateId === templateId)?.title || '')
    : titleFilter.split('&')[0];

  let filteredReports = allReports;
  if (isPLReport && titleFilter) {
    filteredReports = filteredReports.filter(({ title, timeframe }) => {
      if ((timeframe.includes('YTD') && reportType === 'income-statement') || timeframe.toLowerCase() === reportType) {
        return reportTitleToUrl(title) === filteredTitleFilter;
      }
      return false;
    });
  }

  const prevReportType = usePrevious(reportType);
  const dateRange = urlParams.get('dateRange');

  useEffect(() => {
    rangeNoData && navigateTo(`/app/location/${currentLocationId}/report/${reportType}`, { replace: true });
  }, [rangeNoData]);

  const toggleValidate = () => {
    setShowValidationPane(false);
  };

  const handleValidateReport = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const validate = searchParams.get('validate');
    if (validate) {
      setShowValidationPane(true);
      return;
    }
    searchParams.set('validate', 'true');
    const newSearch = searchParams.toString();

    navigateTo({
      search: newSearch,
    });
  };

  const handleBgAuditReport = async () => {
    const nextState = auditMode === 'true' ? 'false' : 'true';
    setAuditMode(nextState);
  };

  const toggleAuditMode = () => {
    const nextState = auditMode === 'true' ? 'false' : 'true';
    setAuditMode(nextState);
    return nextState;
  };

  const handleAuditReport = async (toggle = true) => {
    let next: 'true' | 'false' = 'false';
    if (toggle) {
      next = toggleAuditMode();
    }
    if (!toggle || next === 'true') {
      await getAuditReport(selectedReport?.id as string);
    } else {
      useReportsStore.setState({ auditReport: null });
    }
  };

  const handleAPReportChange = async (reportType: 'AP Aging' | VendorTemplate, configData?: { dateRange: number }) => {
    setCurrentAPReport(reportType);

    // Store configuration values in URL only when they differ from defaults
    const selectedConfigId = reportType === 'AP Aging' ? 'ap-aging' : reportType;
    const limitValue = configData?.dateRange || 4;

    // Set all query parameters in a single operation to avoid race conditions
    const currentParams = qs.parse(location.search, { ignoreQueryPrefix: true });
    const newParams: Record<string, string | undefined> = {};

    // Copy existing params as strings
    Object.entries(currentParams).forEach(([key, value]) => {
      if (typeof value === 'string') {
        newParams[key] = value;
      }
    });

    // Set our specific params
    newParams.configurationId = selectedConfigId;

    // Only include limit if it's not the default value
    if (limitValue !== 4 && selectedConfigId === 'Vendor Invoices') {
      newParams.limit = limitValue.toString();
    } else {
      delete newParams.limit;
    }

    // Update URL with all parameters in a single navigation - let useEffect handle the fetching
    navigateTo(
      {
        pathname: location.pathname,
        search: qs.stringify(newParams, { skipNulls: true }),
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (reportConfiguration) {
      setReportsConfiguration(reportConfiguration as ReportConfiguration);
    }
  }, [reportConfiguration]);

  useEffect(() => {
    if (auditMode === 'true') {
      handleAuditReport(false);
    }
    () => {
      useActiveReportStore.setState({ currentReport: undefined });
    };
  }, []);

  useEffect(() => {
    if (validation) {
      setShowValidationPane(true);
    }
  }, [validation]);

  const handleRefreshReport = async (e: SyntheticEvent<HTMLButtonElement>) => {
    try {
      const target = e.currentTarget;
      // allow only every two minutes

      const selectedReportId = selectedReport?.id;
      const updatedAt = currentReport?.updatedAt ? currentReport?.updatedAt : selectedReport?.updatedAt;
      const difference = updatedAt ? differenceInSeconds(new Date(), parseJSON(updatedAt)) : 0;

      const refreshTimeAllowed = 60 * 5;
      if (difference < refreshTimeAllowed) {
        showToast(`Report can be refreshed again in ${refreshTimeAllowed - difference} seconds`, TOAST_ERROR);
        return;
      }

      if (target) {
        target.disabled = true;
      }

      if (!selectedReportId) {
        return;
      }
      setRefreshingReport(true);
      const periodStartEndDates = currentReport?.dates;
      const extractedReportIds = extractReportIds(periodStartEndDates!);

      const reportIds = extractedReportIds.length > 0 ? extractedReportIds : [selectedReportId];
      await performReportUpdate(selectedReport!, reportIds);

      if (target) {
        target.disabled = false;
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
      toast.error('Error refreshing report');
    } finally {
      setRefreshingReport(false);
    }
  };

  const getReportChildren = (reports: NestedHoneReportSection[], flatArray: Report[] = []): Report[] => {
    for (const report of reports) {
      const maskedTitle = Array(report.level).fill(' * ').join('') + report.title;
      const maskedPercentageTitle = Array(report.level).fill(' * ').join('') + '% OF ' + report.title;

      flatArray.push({
        ...report,
        ...report.data?.map(data => (showMoneyValues ? currencyFormatter2Decimals(data?.amount) : '')),
        title: maskedTitle,
      });

      if (report.title !== '' && showPercentageValues) {
        flatArray.push({
          ...report,
          ...report.data?.map(data => percentageFormatter(data?.perc || 0)),
          title: maskedPercentageTitle,
        });
      }

      if (report.children) {
        getReportChildren(report.children, flatArray);
      }
    }
    return flatArray;
  };

  useEffect(() => {
    if (!isYtd) return;
    if (!selectedReport) return;
    const yearToPlot = Number(selectedReport.startDateText.split('-')[0]);
    setYearPlotted(yearToPlot);
  }, [selectedReport]);

  const validateParsed = urlParams.get('validate');

  useEffect(() => {
    if (isBalanceSheetPreset && reportConfiguration) {
      const type = mappedReports[reportType as keyof typeof mappedReports];
      setLocalLoading(true);
      const preset = findBSPresetDates(
        BALANCE_SHEET_PRESETS[reportId as keyof typeof BALANCE_SHEET_PRESETS],
        reportId as BalanceSheetPreset,
        currentLocation?.weekStart,
        (reportConfiguration as ReportConfiguration)?.data
      );
      const startDate = preset.start;
      const endDate = preset.end;

      (async () => {
        const reportPayload: ReportRequest = {
          type,
          locations: [String(currentLocationId) || ''],
          templateId: templateId,
          startDate,
          endDate,
          postTransforms: {
            audit: auditMode === 'true',
            validate: validateParsed === 'true',
          },
        };
        setReportPayload(reportPayload);

        const response = await getGroupReport(reportPayload);
        setLocalLoading(false);
        if (response === 'error') {
          navigateTo(`/app/location/${currentLocationId}`, { replace: true });
        }
      })();
    }
  }, [isBalanceSheetPreset, reportId, reportConfiguration, auditMode, validateParsed]);

  // Track which report has been validated to detect report changes
  const validatedReportIdRef = useRef<string | null>(null);

  // Handle validation for non-balance-sheet reports (Cash Flow, AP Aging, etc.)
  // This triggers a refetch with validate=true when the URL param is added or report changes
  useEffect(() => {
    if (!validateParsed) return;
    if (isBalanceSheetPreset) return; // Balance sheet handles validation in its own effect
    if (!selectedReport?.id) return;

    const reportChanged = validatedReportIdRef.current !== selectedReport.id;

    // Skip if validation data already exists for the CURRENT report
    if (validationReportData && !reportChanged) return;

    const report = allReports.find(r => r.id === selectedReport.id);
    if (!report) return;

    // Update ref to track current report being validated
    validatedReportIdRef.current = selectedReport.id;

    // fetchReportById reads validate from URL and adds it to postTransforms
    const response = fetchReportById(report);
    toast.promise(
      response,
      {
        loading: 'Validating report, will take a few seconds...',
        success: 'Report successfully validated',
        error: 'Error validating report',
      },
      {
        style: {
          maxWidth: 'max(50vw, 350px)',
        },
      }
    );
  }, [validateParsed, isBalanceSheetPreset, selectedReport?.id]);

  useEffect(() => {
    if (!allReports.length) return;

    // Handle Vendor Invoices configuration - for both deep-linking and user interactions
    let selectedReport: HoneReportSummary | undefined;
    selectedReport = reports.get(reportType!)![0];
    if (configurationId === 'Vendor Invoices') {
      const limitValue = limit || 4;
      const reportPayload: ReportRequest = {
        type: 'AP Aging',
        locations: [currentLocationId || ''],
        templateId: configurationId,
        startDate: commonUtil.toShortString(new Date()),
        limit: limitValue,
        postTransforms: {
          audit: auditMode === 'true',
        },
      };

      // Create a unique key for this request to prevent duplicates
      const requestKey = `${configurationId}-${limitValue}-${currentLocationId}-${auditMode}`;
      if (lastRequestRef.current === requestKey) {
        return; // Skip duplicate request
      }
      lastRequestRef.current = requestKey;
      setReportPayload(reportPayload);
      setOtherReportPayload({
        type: mappedReports[reportType as keyof typeof mappedReports],
        locations: [currentLocationId || ''],
        baseId: currentLocationId || '',
      });
      useReportsStore.setState({ selectedReport: reports.get(reportType!)![0] });
      getGroupReport(reportPayload);
      return;
    } else {
      // Reset the lastRequestRef when switching away from Vendor Invoices
      // to allow future switches to work correctly
      const wasVendorInvoices = lastRequestRef.current?.startsWith('Vendor Invoices');
      if (wasVendorInvoices) {
        lastRequestRef.current = null;
      }

      const localBaseId = reportId || selectedReport?.id;
      if (mappedReports[reportType as keyof typeof mappedReports] !== ReportTypes.BalanceSheet) {
        setOtherReportPayload({
          type: mappedReports[reportType as keyof typeof mappedReports],
          locations: [currentLocationId || ''],
          baseId: localBaseId,
        });
      }

      // When switching back to AP Aging from Vendor Invoices, explicitly fetch the report
      if (wasVendorInvoices && reportType === 'ap-aging') {
        const apAgingReport = reports.get(reportType)?.[0];
        if (apAgingReport) {
          // Clear the current report to trigger loading state
          useActiveReportStore.setState({ currentReport: undefined });
          // Fetch the AP Aging report
          fetchReportById(apAgingReport);
        }
      }
    }

    if (reportType) {
      if (['balance-sheet', 'cash-flow'].includes(reportType) && !reportId) {
        navigateTo(`/app/location/${locationId}/report/${reportType}/6-months`, { replace: true });
        return;
      }
      if (prevReportType !== reportType) {
        setYearPlotted(new Date().getFullYear());
      }

      if (reportId) {
        if (Object.keys(BALANCE_SHEET_PRESETS).includes(reportId)) {
          useReportsStore.setState({ selectedReport });
          return;
        }

        const parsedReportId = reportId.includes('?') ? reportId.split('?')[0] : reportId;
        if (templateId && !dateRange) {
          selectedReport = reports.get(reportType)!.find(report => {
            return report.id === parsedReportId && report.templateId === templateId;
          });
        } else {
          const reportFound = reports.get(reportType)!.find(report => report.id === parsedReportId);

          if (!dateRange && !reportFound) {
            Sentry.captureException(`Report not found ${reportId}`);
            setTimeout(() => {
              showToast(
                "The report you are trying to access isn't available. Please click on another report to access the data you want.",
                TOAST_ERROR
              );
            }, 1);

            return;
          }
          selectedReport = reportFound;
        }
      } else {
        if (titleFilter && !dateRange) {
          selectedReport = reports
            .get(reportType)!
            .find(report => filteredTitleFilter === reportTitleToUrl(report.title));
        }
      }
      if (!selectedReport && !dateRange) {
        try {
          setTimeout(() => {
            showToast(
              "The report you are trying to access isn't available. Please click on another report to access the data you want.",
              TOAST_ERROR,
              FIVE_SECONDS
            );
          }, 1);

          const lookingForId = reportId || titleFilter;
          throw new ReportNotFoundError(lookingForId);
        } catch (error) {
          Sentry.captureException(error);
          navigateTo(`/app/location/${currentLocationId}`);
        }
      }

      if (useReportsStore.getState().selectedReport?.id !== selectedReport?.id) {
        useReportsStore.setState({ selectedReport });
      }
      if (selectedReport?.id) {
        useActiveReportStore.setState({ currentReportId: selectedReport.id });
      }
    }
  }, [
    allReports,
    reports,
    reportId,
    reportType,
    configurationId,
    limit,
    currentLocationId,
    templateId,
    dateRange,
    titleFilter,
    filteredTitleFilter,
    auditMode,
  ]);

  const years = useMemo(
    () =>
      allReports.map(report => ({
        period: new Date(report.endDate).getFullYear(),
        reportId: report.id,
      })),
    [allReports]
  );

  const handleReportChange = (newReportId: string) => {
    if (newReportId === 'custom_range') {
      setOpenDateRangeModal(true);
      return;
    }
    setLocalLoading(true);
    setStatus('loading');
    setDateRange(undefined);

    const yearPlotted = years.find(year => year.reportId === newReportId);
    yearPlotted?.period && setYearPlotted(yearPlotted?.period);

    const { dateRange, total, difference, ...queryParams } = qs.parse(location.search, { ignoreQueryPrefix: true });
    const newQueries = { ...queryParams };

    if (templateId) {
      newQueries.templateId = templateId;
    }
    useReportsStore.setState({ selectedReport: undefined });
    useActiveReportStore.setState({ currentReport: undefined });
    navigateTo({
      pathname: `/app/location/${currentLocationId}/report/${reportType}/${newReportId}`,
      search: qs.stringify(newQueries, { skipNulls: true }),
    });
  };

  // Disable smoothing for Balance Sheet and Cash Flow
  useEffect(() => {
    if (selectedReport && selectedReport?.type === HoneReportTypes.IncomeStatement)
      useReportGraphStore.setState({ extraReportId: undefined });
    if (
      selectedReport &&
      (selectedReport.type === HoneReportTypes.BalanceSheet || selectedReport.type === HoneReportTypes.CashFlow)
    ) {
      useActiveReportStore.setState({ smoothingEnabled: false });
    }
  }, [selectedReport]);

  const hasCharts =
    !isEmpty(allCategories) && selectedReport && ['P&L Comparison', 'Income Statement'].includes(selectedReport?.type);

  const showCharts = chartCollapsed === 'false' && auditMode !== 'true' && hasCharts && reportType !== 'ap-aging';

  const getFilteredReportsOptions = () => {
    if (selectedReport?.type === HoneReportTypes.BalanceSheet || selectedReport?.type === HoneReportTypes.CashFlow) {
      const startDateText = '',
        endDateText = '';
      const bsPresetReports = Object.entries(BALANCE_SHEET_PRESETS).map(([id, preset]) =>
        Object.assign(preset, { id, startDateText, endDateText })
      );
      return [...bsPresetReports, ...filteredReports];
    }

    return filteredReports;
  };

  const filteredReportsOptions = getFilteredReportsOptions();

  const getSelectedReportId = useCallback(() => {
    if (reportId) return reportId.includes('?') ? reportId.split('?')[0] : reportId;
    if (dateRange) return 'custom_range';

    if (status === 'pending') return 'loading';

    if (selectedReport?.type === HoneReportTypes.PLComparison) {
      // check if index exists in array of reports
      return filteredReportsOptions[0].id;
    }

    const index = filteredReportsOptions.findIndex(report => report.id === selectedReport?.id);
    if (index > -1) {
      return filteredReportsOptions[index].id;
    }
    return filteredReportsOptions[0].id;
  }, [selectedReport]);

  if (reportsError) {
    return (
      <div className="report-view add-padding">
        <div className="report-view--header">
          <div />
          <div className="report-view--header__location"></div>
        </div>
        <div className="report-view-container h-100 center">{reportsError}</div>
      </div>
    );
  }

  // Render SlidingPane for validation results - must be outside the loading check
  // so it can display even while the report is loading/validating
  const isValidationLoading = showValidationPane && !validationReportData && !validateReportError;
  const validationPane = (
    <SlidingPane onRequestClose={toggleValidate} title={`Validation results`} width={'75%'} isOpen={showValidationPane}>
      {isValidationLoading && <Loading />}
      {validateReportError && <span>{validateReportError}</span>}
      {validationReportData && <ValidationReportResults reportData={validationReportData} />}
    </SlidingPane>
  );

  // Show loading only if we don't have a current report to display
  // During validation, we keep the current report visible so skip the loading screen
  const isValidating = validateParsed === 'true';
  const isLoading = localLoading || (reportStatus === 'loading' && !currentReport);
  if (isLoading && !isValidating) {
    return (
      <>
        <div className="h-100">
          <Loading />
        </div>
        {validationPane}
      </>
    );
  }

  const Icon: any = selectedReport && selectedReport.type in MENU_ICONS ? MENU_ICONS[selectedReport.type] : null;
  const rTitle: string = selectedReport && typeof selectedReport.type === 'string' ? selectedReport.type : 'P&L';

  const adminTools: ActionProps[] = [
    {
      handleClick: handleValidateReport,
      tooltip: 'Validate report',
      icon: <IconValidate />,
      placement: 'top',
    },
  ];

  if (selectedReport && selectedReport.type === HoneReportTypes.BalanceSheet) {
    adminTools.push({
      handleClick: handleBgAuditReport,
      tooltip: 'Audit Report',
      icon: <IconAuditNew />,
      placement: 'bottom',
      disabled: selectedReport?.type !== HoneReportTypes.BalanceSheet,
    });
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="report-view">
        <KSHeader />
        <div className="report-view--header add-padding">
          <div className="report-view--header__item">
            <span className="header-icon header-icon-bottom ">{Icon && <Icon />}</span>
            <div className="report-view-header-title">
              {enableVendorReports && isBookkeeper && reportType === 'ap-aging' ? (
                <APAgingReportConfiguration
                  currentReport={currentAPReport}
                  onReportChange={handleAPReportChange}
                  configurationId={configurationId || 'ap-aging'}
                  limit={limit || 4}
                />
              ) : (
                rTitle
              )}
            </div>

            {reportType !== 'ap-aging' && (
              <ReportDatesModal
                key={dateRange}
                uniqueTitles={uniqueTitles}
                timeframe={selectedReport && selectedReport.timeframe}
                selectedReportTitle={selectedReport ? selectedReport.title : ''}
                onLoadMore={() => setAllReportsSelected('true')}
                selectedOption={getSelectedReportId()}
                onReportChange={handleReportChange}
                options={filteredReportsOptions}
              />
            )}
          </div>
          <div className="report-view--header__item">
            <div>
              {selectedReport && (
                <STBTools
                  adminMode={isBookkeeper}
                  status={(statusUpdates[selectedReport.id] || selectedReport.status) as HoneReportStatus}
                  onRefresh={(e: SyntheticEvent<HTMLButtonElement, Event>) => handleRefreshReport(e)}
                  onDownload={() => {}}
                  adminTools={adminTools}
                  report={currentReport}
                />
              )}
            </div>
          </div>
        </div>
        {selectedReport && (
          <div className="report-view-container">
            {
              <>
                {selectedReport.format === HoneReportFormats.JSON && (
                  <ReportDataTable
                    report={selectedReport}
                    columnHeaderTitle={columnHeaderTitle}
                    customHeaders={customHeaders || []}
                    hasCharts={!!showCharts}
                    selectedReportType={selectedReport.type}
                  />
                )}
              </>
            }
          </div>
        )}
        {validationPane}
      </div>
    </ErrorBoundary>
  );
}

const OtherReportsViewWrapped = (props: Props) => (
  <LocalErrorBoundary>
    <OtherReportsView {...props} />
  </LocalErrorBoundary>
);

export default OtherReportsViewWrapped;
